import { cloneObj, exists, Module } from '@nu-art/ts-common';
import { NotesRenderKey } from './types.js';
import { dispositionType_Admit, dispositionType_Discharge, PanelKey_Home, PanelKey_SmartDocs } from '@app/advisor-v5-shared';
import { smartDocParamCondition_DispositionType_Admit, smartDocParamCondition_DispositionType_Discharge } from '@app/advisor-content-shared';
import { ModuleFE_EMInterface, SessionKey_AdvisorConfig } from '@app/advisor-frontend';
import { ThunderDispatcher } from '@nu-art/thunderstorm-frontend';
import { clearDispositionSections } from './utils.js';
import { ModuleFE_AdvisorMini } from '../ModuleFE_AdvisorMini.js';
import { Board_SmartDocs } from '../../_enum.js';
import { ModuleFE_AdvisorAnalytics } from '../../_analytics/consts.js';
const dispatch_OnRenderKeyChange = new ThunderDispatcher('__onNotesRenderKeyChange');
class ModuleFE_Notes_Class extends Module {
    _stampedNote;
    _allowedTemplateIds = undefined;
    _stage = {};
    _renderKey = NotesRenderKey.Notes;
    //######################### Allowed Template IDs #########################
    get allowedTemplateIds() {
        if (!this._allowedTemplateIds)
            this._allowedTemplateIds = SessionKey_AdvisorConfig.get()?.noteIds;
        return [...(this._allowedTemplateIds ?? [])];
    }
    //######################### Stage #########################
    get selectedTemplateId() {
        return this._stage.templateId;
    }
    set selectedTemplateId(id) {
        this._stage.templateId = id;
    }
    get dispositionType() {
        return this._stage.dispositionType;
    }
    set dispositionType(type) {
        this._stage.dispositionType = type;
    }
    get diagnosisId() {
        return this._stage.diagnosisId;
    }
    clearStage() {
        this.selectedTemplateId = undefined;
        this.dispositionType = undefined;
        this.setDiagnosis(undefined, undefined);
    }
    setDiagnosis(id, label) {
        this._stage.diagnosisId = id;
        this._stage.diagnosisLabel = label;
    }
    //######################### Render Key #########################
    get renderKey() {
        return this._renderKey;
    }
    set renderKey(key) {
        this._renderKey = key;
        dispatch_OnRenderKeyChange.dispatchAll();
    }
    //######################### Stamped Note #########################
    get stampedNote() {
        return this._stampedNote ? cloneObj(this._stampedNote) : undefined;
    }
    clearStampedNote() {
        this._stampedNote = undefined;
        this.renderKey = NotesRenderKey.Notes;
    }
    async generateStampedNote(templateId, openMethod, protocol, dispositionType) {
        const start = Date.now();
        const request = { noteTemplateId: templateId };
        request.dynamicParams = {
            dispositionType: dispositionType === dispositionType_Admit
                ? smartDocParamCondition_DispositionType_Admit
                : dispositionType === dispositionType_Discharge
                    ? smartDocParamCondition_DispositionType_Discharge
                    : undefined,
        };
        if (protocol)
            request.dynamicParams.workingDiagnosis = protocol.pathway._id;
        try {
            const response = await ModuleFE_EMInterface.vv1.smartNote.get(request).executeSync();
            const elapsed_ms = Date.now() - start;
            this._stampedNote = {
                note: response.note,
                elapsed_ms,
                dispositionType: dispositionType,
                openMethod: openMethod,
                workingDiagnosis: protocol ? { id: protocol.pathway._id, label: protocol.getLabel() } : undefined,
            };
            this.clearStage();
            this.renderKey = NotesRenderKey.Review;
            return {
                status: 'success',
                note: this.stampedNote,
                elapsedMS: elapsed_ms,
            };
        }
        catch (err) {
            this.logError('Could not generate stamped note', err);
            return { status: 'fail', elapsedMS: Date.now() - start };
        }
    }
    clearStampedNoteDisposition() {
        if (!this._stampedNote)
            return false;
        const cleared = clearDispositionSections(this._stampedNote.note.sections);
        if (!cleared)
            return false;
        delete this._stampedNote.dispositionType;
        delete this._stampedNote.workingDiagnosis;
        return true;
    }
    removeStampedNoteDisposition(aiText) {
        if (!this._stampedNote)
            return;
        this._stampedNote.note.aiText = aiText;
        delete this._stampedNote.dispositionType;
        delete this._stampedNote.workingDiagnosis;
    }
    async sendToEHR() {
        if (!this.stampedNote)
            return false;
        try {
            await ModuleFE_EMInterface.vv1.smartNote.writeBack({ note: this.stampedNote.note }).executeSync();
            this.renderKey = NotesRenderKey.Sent;
            return true;
        }
        catch (err) {
            this.logError('Could not send note to EHR', err);
            return false;
        }
    }
    //######################### General #########################
    closeNotesPanel = () => {
        if (ModuleFE_AdvisorMini.getIsMiniActive())
            ModuleFE_AdvisorMini.openPanel(PanelKey_Home);
        else
            Board_SmartDocs.close();
    };
    openNotesPanel = () => {
        if (ModuleFE_AdvisorMini.getIsMiniActive())
            ModuleFE_AdvisorMini.openPanel(PanelKey_SmartDocs);
        else
            Board_SmartDocs.open();
    };
    analytics = {
        note: {
            open: () => {
                if (!this.stampedNote)
                    return;
                ModuleFE_AdvisorAnalytics.trigger.note.note_open({
                    openMethod: this.stampedNote.openMethod,
                    elapsed_ms: this.stampedNote.elapsed_ms,
                    noteId: this.stampedNote.note.templateId,
                    dispositionType: this.stampedNote.dispositionType,
                    workingDiagnosisId: this.stampedNote.workingDiagnosis?.id,
                    workingDiagnosisLabel: this.stampedNote.workingDiagnosis?.label,
                });
            },
            abandon: () => {
                if (!this.stampedNote)
                    return;
                ModuleFE_AdvisorAnalytics.trigger.note.note_abandon({
                    noteId: this.stampedNote.note.templateId,
                    openMethod: this.stampedNote.openMethod,
                    dispositionType: this.stampedNote.dispositionType,
                    workingDiagnosisId: this.stampedNote.workingDiagnosis?.id,
                    workingDiagnosisLabel: this.stampedNote.workingDiagnosis?.label,
                });
            },
            copy: () => {
                if (!this.stampedNote)
                    return;
                ModuleFE_AdvisorAnalytics.trigger.note.note_copy({
                    noteId: this.stampedNote.note.templateId,
                    dispositionType: this.stampedNote.dispositionType,
                    workingDiagnosisId: this.stampedNote.workingDiagnosis?.id,
                    workingDiagnosisLabel: this.stampedNote.workingDiagnosis?.label,
                });
            },
            sent: () => {
                if (!this.stampedNote)
                    return;
                ModuleFE_AdvisorAnalytics.trigger.note.note_send({
                    noteId: this.stampedNote.note.templateId,
                    openMethod: this.stampedNote.openMethod,
                    dispositionType: this.stampedNote.dispositionType,
                    workingDiagnosisId: this.stampedNote.workingDiagnosis?.id,
                    workingDiagnosisLabel: this.stampedNote.workingDiagnosis?.label,
                });
            },
            select: () => {
                if (!this._stage.templateId)
                    return;
                ModuleFE_AdvisorAnalytics.trigger.note.note_template_select({
                    noteId: this._stage.templateId,
                });
            }
        },
        protocolSelection: {
            open: () => {
                if (!this._stage.templateId)
                    return;
                ModuleFE_AdvisorAnalytics.trigger.note.note_disposition_open({ noteId: this._stage.templateId });
            },
            abandon: () => {
                if (!this._stage.templateId)
                    return;
                ModuleFE_AdvisorAnalytics.trigger.note.note_disposition_abandon({ noteId: this._stage.templateId });
            },
            submit: () => {
                if (!this._stage.templateId || !this._stage.dispositionType)
                    return;
                ModuleFE_AdvisorAnalytics.trigger.note.note_disposition_submit({
                    noteId: this._stage.templateId,
                    diagnosisSource: exists(this._stage.diagnosisId) ? 'ddx' : 'other',
                    dispositionType: this._stage.dispositionType,
                    workingDiagnosisId: this._stage.diagnosisId,
                    workingDiagnosisLabel: this._stage.diagnosisLabel,
                });
            },
            select: () => {
                if (!this._stage.templateId)
                    return;
                ModuleFE_AdvisorAnalytics.trigger.note.note_disposition_select({
                    noteId: this._stage.templateId,
                    workingDiagnosisId: this._stage.diagnosisId,
                    workingDiagnosisLabel: this._stage.diagnosisLabel,
                });
            }
        }
    };
}
export const ModuleFE_Notes = new ModuleFE_Notes_Class();
//# sourceMappingURL=ModuleFE_Notes.js.map